import React from "react";
import PropTypes from "prop-types";
import { HelpCircle, X, LogOut, ChevronRight } from "lucide-react";
import { cn } from "../../lib/utils";
import { Avatar, AvatarFallback } from "../ui/avatar";
import { useMobileDetection } from "../../hooks/useMobileDetection";
import { LightTooltip } from "../ui/tooltip";

/**
 * NeedHelpButton - light pill CTA pinned at the bottom of the aside rail / drawer.
 * Default footer for SidebarV3 and MobileDrawerV3.
 */
export function NeedHelpButton({ onClick, label = "Need Help", className = "" }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "w-full flex items-center justify-center gap-2 rounded-xl bg-neutral-1 text-neutral-12",
        "py-3 px-4 text-sm font-medium shadow-sm hover:shadow-md transition-shadow",
        className
      )}
    >
      <HelpCircle className="w-4 h-4" />
      <span>{label}</span>
    </button>
  );
}
NeedHelpButton.propTypes = {
  onClick: PropTypes.func,
  label: PropTypes.string,
  className: PropTypes.string,
};

/**
 * SidebarDividerV3 - thin white separator between nav sections inside SidebarV3.
 * Place between section groups in the children passed to SidebarV3.
 */
export function SidebarDividerV3({ className = "" }) {
  return (
    <div className={cn("mx-4 my-1 border-t border-[#FFFFFF33]", className)} />
  );
}
SidebarDividerV3.propTypes = {
  className: PropTypes.string,
};

/**
 * SidebarV3 - collapsible navy aside rail (desktop only; hidden < lg).
 *
 * Collapse behaviour:
 *  - Expanded (default): 280 px wide — logo, labels, section titles visible.
 *  - Collapsed: 72 px wide — only icons visible; labels/titles fade out.
 *  - Transition: CSS `transition-[width]` + `transition-opacity` for smooth animation.
 *
 * Controlled collapse: pass `collapsed` + `onToggle`. The chevron toggle button
 * is always rendered inside the sidebar so consumers don't need to wire it
 * externally.
 *
 * Slots:
 *  - logo:      node rendered at the top (hidden in collapsed mode)
 *  - topAction: node under the logo (hidden in collapsed mode)
 *  - children:  scrollable middle content (passed collapsed state via render-prop if needed externally)
 *  - footer:    bottom slot. Omit → defaults to <NeedHelpButton/>; pass `null` to hide.
 */
export function SidebarV3({
  logo,
  topAction,
  children,
  footer,
  onNeedHelpClick,
  collapsed = false,
  onToggle,
  className = "",
  contentClassName = "",
}) {
  const { isMobile } = useMobileDetection();

  if (isMobile) return null;

  return (
    <aside
      className={cn(
        "flex flex-col h-full flex-shrink-0 relative",
        "bg-secondary-2 text-white border-r border-white/10",
        className
      )}
      style={{ width: collapsed ? "72px" : "280px", transition: "width 500ms ease-in-out" }}
    >
      {/* Logo row + collapse/expand control */}
      <div
        className={cn(
          "flex-shrink-0 flex items-center h-[84px]",
          "transition-all duration-500 ease-in-out",
          collapsed
            ? "px-3 justify-center"
            : "px-6 justify-between"
        )}
      >
        {/* Logo — fades out when collapsed */}
        <div
          className={cn(
            "overflow-hidden transition-all duration-500 ease-in-out",
            collapsed ? "opacity-0 w-0 pointer-events-none" : "opacity-100"
          )}
        >
          {logo}
        </div>

        {/* Toggle: X (collapse) when expanded, ChevronRight (expand) when collapsed */}
        {onToggle && (
          <button
            type="button"
            onClick={onToggle}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            className="p-1.5 rounded-md text-white/70 hover:text-white hover:bg-white/10 transition-colors flex-shrink-0"
          >
            {collapsed ? (
              <ChevronRight className="w-6 h-6" />
            ) : (
              <X className="w-6 h-6" />
            )}
          </button>
        )}
      </div>

      {/* Divider: logo → content */}
      <div className="border-t border-[#FFFFFF33] flex-shrink-0" />

      {/* topAction — hidden when collapsed */}
      {topAction && (
        <div
          className={cn(
            "px-6 pb-4 flex-shrink-0 overflow-hidden",
            "transition-all duration-500 ease-in-out",
            collapsed ? "opacity-0 h-0 py-0 pointer-events-none" : "opacity-100"
          )}
        >
          {topAction}
        </div>
      )}

      {/* Scrollable content area */}
      <div
        className={cn(
          "flex-1 overflow-y-auto py-2 scroll-hide",
          "transition-[padding] duration-500 ease-in-out",
          collapsed ? "px-2" : "px-4",
          contentClassName
        )}
      >
        {children}
      </div>

      {/* Footer: Need Help — icon-only when collapsed, full when expanded */}
      {(footer !== null) && (
        collapsed ? (
          <div className="px-2 pb-4 pt-3 flex-shrink-0 flex justify-center">
            <LightTooltip title="Need Help" side="right" align="center" variant="dark" sideOffset={8}>
              <button
                type="button"
                onClick={onNeedHelpClick}
                aria-label="Need Help"
                className="w-full flex items-center justify-center p-2 rounded-xl text-white/70 hover:text-white hover:bg-white/10 transition-colors"
              >
                <HelpCircle className="w-5 h-5" />
              </button>
            </LightTooltip>
          </div>
        ) : (
          <div className="px-4 pb-4 pt-3 flex-shrink-0 transition-all duration-500 ease-in-out">
            {footer === undefined ? (
              <NeedHelpButton onClick={onNeedHelpClick} />
            ) : (
              footer
            )}
          </div>
        )
      )}
    </aside>
  );
}
SidebarV3.propTypes = {
  logo: PropTypes.node,
  topAction: PropTypes.node,
  children: PropTypes.node,
  footer: PropTypes.node,
  onNeedHelpClick: PropTypes.func,
  collapsed: PropTypes.bool,
  onToggle: PropTypes.func,
  className: PropTypes.string,
  contentClassName: PropTypes.string,
};

/**
 * MobileDrawerV3 - light off-canvas drawer for < lg screens.
 *
 * Matches the mobile Figma: ✕ + centered logo on top, optional user section,
 * a free `children` content slot, and an optional Logout pinned at the bottom.
 * Controlled via `isOpen` / `onClose`.
 */
export function MobileDrawerV3({
  isOpen = false,
  onClose,
  logo,
  user,
  onLogout,
  children,
  className = "",
}) {
  const { isMobile } = useMobileDetection();

  if (!isMobile) return null;

  return (
    <div aria-hidden={!isOpen}>
      {/* Overlay */}
      <div
        className={cn(
          "fixed inset-0 bg-black/50 z-40 transition-opacity duration-300",
          isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        )}
        onClick={onClose}
      />

      {/* Panel: full-width so it covers the whole screen on any phone size */}
      <div
        className={cn(
          "fixed inset-y-0 left-0 w-full bg-neutral-1 z-50 flex flex-col shadow-2xl",
          "transition-transform duration-300 ease-in-out",
          isOpen ? "translate-x-0" : "-translate-x-full",
          className
        )}
      >
        {/* Top: close | centered logo | balance spacer */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-5 flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            aria-label="Close menu"
            className="p-1 rounded-md hover:bg-neutral-2 transition-colors"
          >
            <X className="w-6 h-6 text-neutral-12" />
          </button>
          {logo}
          <div className="w-8 h-8" />
        </div>

        {/* User */}
        {user && (
          <button
            type="button"
            onClick={() => {
              user.onProfileClick?.();
              onClose?.();
            }}
            className="w-full px-5 py-6 text-left hover:bg-neutral-2 transition-colors flex-shrink-0 border-b border-neutral-5"
          >
            <div className="flex items-center gap-4">
              <Avatar className="w-12 h-12 flex items-center justify-center bg-secondary-6">
                <AvatarFallback className="text-secondary-2 text-base font-normal">
                  {user.initials}
                </AvatarFallback>
              </Avatar>
              <span className="text-xl font-medium text-neutral-12">
                {user.name}
              </span>
            </div>
          </button>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>

        {/* Logout */}
        {onLogout && (
          <div className="border-t border-neutral-5 flex-shrink-0">
            <button
              type="button"
              onClick={() => {
                onLogout?.();
                onClose?.();
              }}
              className="w-full flex items-center gap-4 px-5 py-6 hover:bg-neutral-2 transition-colors"
            >
              <div className="bg-label-red-1 p-2.5 rounded-xl">
                <LogOut className="w-7 h-7 text-label-red-2" />
              </div>
              <span className="text-base text-neutral-12">Logout</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
MobileDrawerV3.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  logo: PropTypes.node,
  user: PropTypes.shape({
    initials: PropTypes.string,
    name: PropTypes.string,
    onProfileClick: PropTypes.func,
  }),
  onLogout: PropTypes.func,
  children: PropTypes.node,
  className: PropTypes.string,
};
